import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { apiFetch } from '../../../context/AuthContext';
import PrintablePayslip from '../components/PrintablePayslip';
import { translations, getMonthName } from '../config/translations_Salary';
import { normalizePayrollData } from '../hooks/usePayrollData';
import { buildCalculatedValues, toInsuranceSalary } from './taxCalculations';
import { exportPayslipPdf, payslipFileName } from './payslipPdf';

// Payslip PDF downloads that happen OUTSIDE the salary page:
//   • downloadPayslipPdf         — the signed-in employee's own payslip (the
//                                  "salary paid" notification)
//   • downloadEmployeePayslipPdf — any payroll row, for admins/managers on the
//                                  Payroll page ("Print payslip" row action)
//
// Both render the exact same PrintablePayslip the salary page uses into a
// detached, off-screen React root and feed it to the same PDF engine, so the
// file is identical to the one from the page's "Print payslip" button.
// Read-only: never creates a missing payroll.

async function renderAndExportPayslip({ row, identity, netSalary, insuranceSalary, language, year, month }) {
  const payrollData = normalizePayrollData(row);
  const isRTL = language === 'ar';
  const t = translations[language] || translations.en;

  const host = document.createElement('div');
  host.style.cssText = 'position:absolute;left:-9999px;top:0;width:800px;';
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    flushSync(() => {
      root.render(
        <PrintablePayslip
          payrollData={payrollData}
          employee={identity}
          theme="light"
          t={t}
          isRTL={isRTL}
          selectedMonth={Number(month)}
          selectedYear={Number(year)}
          calculatedValues={buildCalculatedValues(netSalary, insuranceSalary)}
        />,
      );
    });
    const el = host.querySelector('.printable-payslip');
    const name = payrollData?.employeeName || identity.arabicName || identity.infinityCode;
    await exportPayslipPdf(el, { isRTL, fileName: payslipFileName(name, getMonthName(Number(month), t), year) });
  } finally {
    root.unmount();
    host.remove();
  }
}

export async function downloadPayslipPdf({
  year,
  month,
  language = 'ar',
  profile = null,
  apiBaseUrl = process.env.REACT_APP_BASE_URL,
}) {
  // Same identity sources as the salary page's usePayrollData.
  const infinityCode = profile?.infinityCode || localStorage.getItem('infinityCode') || '';
  const clientCode = profile?.clientCode || localStorage.getItem('clientCode') || infinityCode;
  // Server-confirmed name only — never a stored or placeholder one.
  const arabicName = profile?.employeeArabicName || profile?.employeeName || '';
  const netSalary = parseFloat(profile?.netSalary) || 0;
  const insuranceSalary = toInsuranceSalary(profile?.insuranceSalary); // none on file = 0, never the net
  if (!infinityCode) throw new Error('Missing employee code');

  const res = await apiFetch(`${apiBaseUrl}/payrollX/employee/${infinityCode}?year=${year}&month=${month}`, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const result = await res.json();
  const row = Array.isArray(result?.data) ? result.data[0] : null;
  if (!row) throw new Error('Payslip not found for this period');

  await renderAndExportPayslip({
    row, identity: { infinityCode, clientCode, arabicName }, netSalary, insuranceSalary, language, year, month,
  });
}

/**
 * Admin/manager: download the payslip of ONE payroll row (by payroll id).
 * Uses GET /payrollX/:id (payroll "view" permission — the same permission the
 * payroll table itself needs), and takes the salary figures from that
 * employee's own record, exactly as their own salary page would.
 */
export async function downloadEmployeePayslipPdf({
  payrollId,
  language = 'ar',
  apiBaseUrl = process.env.REACT_APP_BASE_URL,
}) {
  if (!payrollId) throw new Error('Missing payroll id');
  const res = await apiFetch(`${apiBaseUrl}/payrollX/${payrollId}`, { method: 'GET' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const result = await res.json();
  const row = result?.data;
  if (!row) throw new Error('Payslip not found');

  const emp = row.employee || {};
  const infinityCode = String(row.infinityCode || emp.infinityCode || '');
  const netSalary = parseFloat(emp.netSalary) || parseFloat(row.basicSalary) || 0;
  const insuranceSalary = toInsuranceSalary(emp.insuranceSalary); // none on file = 0, never the net

  await renderAndExportPayslip({
    row,
    identity: { infinityCode, clientCode: emp.clientCode || infinityCode, arabicName: emp.arabicName || emp.sr || row.employeeName || '' },
    netSalary,
    insuranceSalary,
    language,
    year: row.year,
    month: row.month,
  });
}
